// ─────────────────────────────────────────────
//  useViewport — the live window inner width/height, updated on resize.
//  Used by the TV board to derive a resolution-aware scale factor (Prompt 5)
//  so cards/text/icons/spacing grow on big monitors and shrink on small ones.
// ─────────────────────────────────────────────
import { useEffect, useState } from "react";

const read = () => ({
  width: typeof window !== "undefined" ? window.innerWidth : 0,
  height: typeof window !== "undefined" ? window.innerHeight : 0,
});

export function useViewport() {
  const [size, setSize] = useState(read);
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const onResize = () =>
      setSize((prev) => {
        const next = read();
        return prev.width === next.width && prev.height === next.height ? prev : next;
      });
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return size;
}

// Resolution-aware scale for the TV board. Baseline is 1080p (scale 1). Scales
// by the smaller of the width/height ratios so nothing overflows, clamped to a
// sane range so tiny or huge monitors stay readable. Pure — easy to unit test.
export function tvScaleForViewport(width, height, base = { w: 1920, h: 1080 }) {
  if (!width || !height) return 1;
  const k = Math.min(width / base.w, height / base.h);
  return Math.max(0.7, Math.min(k, 2.2));
}
